const MOBILE_BREAKPOINT = 750;
const FIRST_LINE_DELAY = 240;
const CHARACTER_DURATION = 820;
const CHARACTER_STAGGER = 16;
const LINE_PAUSE = 80;
const CLOSING_DELAY = 250;
const CLOSING_DURATION = 760;
const RESIZE_DEBOUNCE = 140;
const LINE_TOP_TOLERANCE = 2;

class EditorialTextMarqueeReveal extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.connectionToken = (this.connectionToken || 0) + 1;
    this.section = this.querySelector('.editorial-text-marquee');
    this.accessibleStatement = this.querySelector(
      '.editorial-text-marquee__foreground--accessible .editorial-text-marquee__statement'
    );
    this.inversionStatement = this.querySelector(
      '.editorial-text-marquee__foreground--inversion .editorial-text-marquee__statement'
    );
    this.accessibleQuote = this.accessibleStatement?.closest('.editorial-text-marquee__quote');

    if (
      !this.section ||
      !this.accessibleStatement ||
      !this.inversionStatement ||
      !this.accessibleQuote
    ) {
      return;
    }

    if (!this.authoredSegments) {
      this.authoredSegments = [...this.accessibleStatement.querySelectorAll('.editorial-text-marquee__statement-line')]
        .map((line) => line.textContent.trim())
        .filter(Boolean);
      this.accessibleText = this.authoredSegments.join(' ');
    }

    const token = this.connectionToken;
    this.initialize(token);
  }

  disconnectedCallback() {
    this.initialized = false;
    this.connectionToken = (this.connectionToken || 0) + 1;
    this.observer?.disconnect();
    this.resizeObserver?.disconnect();
    this.removeCompletionListener();
    window.clearTimeout(this.fallbackTimer);
    window.clearTimeout(this.resizeTimer);
    window.clearTimeout(this.fontTimer);
    document.fonts?.removeEventListener('loadingdone', this.handleFontLoad);
  }

  async initialize(token) {
    await this.waitForFonts();

    if (!this.isConnected || token !== this.connectionToken) return;

    this.rebuildLines();
    this.observeGeometry();

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const designMode = this.dataset.designMode === 'true';

    if (this.completed || designMode || !('IntersectionObserver' in window)) {
      if (!reducedMotion) this.activateMarquee();
      this.complete();
      return;
    }

    if (reducedMotion) {
      this.complete();
      return;
    }

    this.section.classList.add('is-reveal-ready');
    this.observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          this.reveal();
        }
      },
      { threshold: [0.25] }
    );

    requestAnimationFrame(() => this.observer?.observe(this.section));
  }

  waitForFonts() {
    if (!document.fonts?.ready) return Promise.resolve();

    return Promise.race([
      document.fonts.ready,
      new Promise((resolve) => {
        this.fontTimer = window.setTimeout(resolve, 2000);
      }),
    ]).finally(() => window.clearTimeout(this.fontTimer));
  }

  observeGeometry() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.queueRebuild());
      this.resizeObserver.observe(this.accessibleQuote);
    }

    this.handleFontLoad = () => this.queueRebuild(true);
    document.fonts?.addEventListener('loadingdone', this.handleFontLoad);
  }

  queueRebuild(force = false) {
    const signature = this.getGeometrySignature();

    if (!signature || (!force && signature === this.geometrySignature)) return;

    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.isConnected) return;

      if (this.revealed && !this.completed) {
        this.complete();
      }

      this.rebuildLines();
    }, RESIZE_DEBOUNCE);
  }

  getGeometrySignature() {
    if (!this.accessibleQuote || !this.accessibleStatement) return '';

    const quoteRect = this.accessibleQuote.getBoundingClientRect();
    const styles = getComputedStyle(this.accessibleStatement);

    return [
      Math.round(quoteRect.width * 10) / 10,
      styles.fontFamily,
      styles.fontSize,
      styles.fontWeight,
      styles.lineHeight,
      styles.letterSpacing,
      window.innerWidth < MOBILE_BREAKPOINT ? 'mobile' : 'desktop',
    ].join('|');
  }

  rebuildLines() {
    const lines = this.measureVisualLines();

    if (!lines.length) return;

    const { markup, sequenceDuration } = this.buildAnimatedMarkup(lines);
    this.accessibleStatement.setAttribute('aria-label', this.accessibleText);
    this.accessibleStatement.innerHTML = markup;
    this.inversionStatement.innerHTML = markup;
    this.section.style.setProperty('--editorial-joy-sequence-duration', `${sequenceDuration}ms`);
    this.section.dataset.visualLineCount = String(lines.length);
    this.section.classList.add('is-lines-ready');
    this.sequenceDuration = sequenceDuration;
    this.geometrySignature = this.getGeometrySignature();
  }

  measureVisualLines() {
    const isDesktop = window.innerWidth >= MOBILE_BREAKPOINT;
    const measurer = document.createElement('div');
    const measuredWords = [];

    measurer.className =
      'editorial-text-marquee__statement editorial-text-marquee__statement--measurer';

    this.authoredSegments.forEach((segmentText, segmentIndex) => {
      const segment = document.createElement('span');
      const words = segmentText.split(/\s+/).filter(Boolean);

      segment.className = 'editorial-text-marquee__measurement-segment';
      segment.style.display = isDesktop ? 'block' : 'inline';

      words.forEach((wordText, wordIndex) => {
        const word = document.createElement('span');

        word.className = 'editorial-text-marquee__measurement-word';
        word.dataset.word = wordText;

        [...wordText].forEach((character) => {
          const characterNode = document.createElement('span');

          characterNode.className = 'editorial-text-marquee__measurement-character';
          characterNode.textContent = character;
          word.append(characterNode);
        });

        segment.append(word);
        measuredWords.push(word);

        if (wordIndex < words.length - 1) {
          segment.append(document.createTextNode(' '));
        }
      });

      measurer.append(segment);

      if (!isDesktop && segmentIndex < this.authoredSegments.length - 1) {
        measurer.append(document.createTextNode(' '));
      }
    });

    this.accessibleQuote.append(measurer);

    const lines = [];
    let currentTop = null;
    let currentLine = [];

    measuredWords.forEach((word) => {
      const top = word.getBoundingClientRect().top;

      if (currentTop === null || Math.abs(top - currentTop) <= LINE_TOP_TOLERANCE) {
        currentLine.push(word.dataset.word);
      } else {
        lines.push(currentLine.join(' '));
        currentLine = [word.dataset.word];
      }

      currentTop = top;
    });

    if (currentLine.length) {
      lines.push(currentLine.join(' '));
    }

    measurer.remove();
    return lines;
  }

  buildAnimatedMarkup(lines) {
    const container = document.createElement('div');
    let lineStart = FIRST_LINE_DELAY;

    lines.forEach((lineText, lineIndex) => {
      const lineMask = document.createElement('span');
      const characterCopy = document.createElement('span');
      const words = lineText.split(/\s+/).filter(Boolean);
      let characterIndex = 0;

      lineMask.className = 'editorial-text-marquee__statement-line-mask';
      lineMask.dataset.visualLine = String(lineIndex);
      lineMask.style.setProperty('--editorial-line-start', `${lineStart}ms`);
      characterCopy.className = 'editorial-text-marquee__statement-character-copy';
      characterCopy.setAttribute('aria-hidden', 'true');

      words.forEach((wordText, wordIndex) => {
        const word = document.createElement('span');

        word.className = 'editorial-text-marquee__statement-word';

        [...wordText].forEach((character) => {
          const characterNode = document.createElement('span');

          characterNode.className = 'editorial-text-marquee__statement-character';
          characterNode.style.setProperty('--editorial-character-index', characterIndex);
          characterNode.textContent = character;
          word.append(characterNode);
          characterIndex += 1;
        });

        characterCopy.append(word);

        if (wordIndex < words.length - 1) {
          characterCopy.append(document.createTextNode(' '));
        }
      });

      lineMask.append(characterCopy);
      container.append(lineMask);
      lineStart += CHARACTER_DURATION + Math.max(characterIndex - 1, 0) * CHARACTER_STAGGER + LINE_PAUSE;
    });

    return {
      markup: container.innerHTML,
      sequenceDuration: lineStart,
    };
  }

  reveal() {
    if (this.revealed) return;

    this.revealed = true;
    this.observer?.disconnect();
    this.activateMarquee();
    this.section.classList.add('is-revealing');
    this.completionTarget = this.section.querySelector(
      '.editorial-text-marquee__foreground--accessible .editorial-text-marquee__closing-line'
    );

    if (this.completionTarget) {
      this.handleCompletion = (event) => {
        if (event.target === this.completionTarget) this.complete();
      };
      this.completionTarget.addEventListener('animationend', this.handleCompletion);
    }

    const fallbackDelay = this.sequenceDuration + CLOSING_DELAY + CLOSING_DURATION + 1000;
    this.fallbackTimer = window.setTimeout(() => this.complete(), fallbackDelay);
  }

  activateMarquee() {
    this.section.classList.add('is-marquee-active');
  }

  removeCompletionListener() {
    if (this.completionTarget && this.handleCompletion) {
      this.completionTarget.removeEventListener('animationend', this.handleCompletion);
    }

    this.completionTarget = null;
    this.handleCompletion = null;
  }

  complete() {
    if (this.completed) return;

    this.completed = true;
    this.observer?.disconnect();
    this.removeCompletionListener();
    window.clearTimeout(this.fallbackTimer);
    this.section.classList.remove('is-reveal-ready', 'is-revealing');
    this.section.classList.add('is-reveal-complete');
  }
}

if (!customElements.get('editorial-text-marquee-reveal')) {
  customElements.define('editorial-text-marquee-reveal', EditorialTextMarqueeReveal);
}
